import { DataGridPro, GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import useAuth from 'auth/UseAuth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createdAt,
  failedCount,
  passedCount,
  sampleTrackingQualityCheckUndoDialog,
  sampleTrackingTransitionFileDialog,
  transition,
  transitionId,
  transitionQualityCheckGroupId,
} from '../../util/Constants';
import {
  GetAllTransitionWithQualityCheckGroupsForResearchProject,
  GetQualityCheckGroupCounts,
  GetUndoableQualityCheckGroupIds,
  QualityCheckGroupAndTransition,
  QualityCheckGroupCounts,
  TransitionSampleQualityCheckGroup,
} from '../../data/SampleTrackingQualityCheckGroupData';
import { renderSampleTrackingTransitionSampleQualityCheckGroupScopedModalCell } from '../../components/grid/cell/SampleTrackingQualityCheckGroupSampleGridCell';
import { renderSampleTrackingQualityCheckGroupUndoGridCell } from '../../components/grid/cell/SampleTrackingQualityCheckGroupUndoGridCell';
import { renderSampleTrackingSupplementalFilesGridCell } from '../../components/grid/cell/SampleTrackingSupplementalFilesGridCell';
import { compact, keyBy, uniq } from 'lodash';
import { QualityCheckStatus } from '../../data/SampleTrackingData';
import { dateComparator, dateFormatter, ExtractDisplayValueFromPossibleArray } from '../../util/grid/TableUtils';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { ErrorIndicator } from '../../components/ErrorIndicator';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { UseStateSetter } from '../../util/TypeUtil';
import { truncateGuid } from '../../data/SampleData';
import { useResearchProjectTransitionPath } from '../../components/hooks/UseResearchProjectTransitionPaths';
import { ResearchProjectTransitionPath } from '../../data/ResearchProjectTransitionPathData';

export interface SampleTrackingQualityCheckGridProps {
  researchProjectId: string;
}

type Row = TransitionSampleQualityCheckGroup & {
  id: number;
  researchProjectId: string;
  transitionId: ReadonlyArray<string>;
  transitionEnumId: ReadonlyArray<string>;
  passedCount?: number;
  failedCount?: number;
  supplementalFileCounts?: number;
};

export const SampleTrackingQualityCheckGrid = ({ researchProjectId }: SampleTrackingQualityCheckGridProps) => {
  const { accessToken } = useAuth();

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(true);

  const [transitionPaths] = useResearchProjectTransitionPath(researchProjectId, setLoadingState);

  const columns = useColumns(setRefreshTrigger);
  const [rows, setRows] = useState<GridRowsProp<Row>>([]);

  const getRows = useCallback(
    (
      data: ReadonlyArray<QualityCheckGroupAndTransition>,
      undoableIds: ReadonlyArray<string>,
      counts: ReadonlyArray<QualityCheckGroupCounts>,
      transitionPaths: ReadonlyArray<ResearchProjectTransitionPath>
    ) => {
      let index = 0;

      const qualityCheckStatusCounts = counts.flatMap(i => i.qualityCheckStatusCounts);
      const passedCounts = keyBy(
        qualityCheckStatusCounts.filter(c => c.qualityCheckStatus === ('Passed' as QualityCheckStatus)),
        c => c.qualityCheckGroupId
      );
      const failedCounts = keyBy(
        qualityCheckStatusCounts.filter(c => c.qualityCheckStatus === ('Failed' as QualityCheckStatus)),
        c => c.qualityCheckGroupId
      );
      const countsById = keyBy(counts, i => i.qualityCheckGroupId);
      const transitionById = keyBy(transitionPaths, i => i.configuredTransitionId);

      const rows: Row[] = data.map(r => {
        const transitionId: string[] = compact(uniq(r.transitionDtos.map(s => s.transitionId)));
        const configuredTransitionIds: string[] = compact(uniq(r.transitionDtos.map(s => s.configuredTransitionId)));
        const transitionType = compact(uniq(r.transitionDtos.map(s => s.transition)));

        const qualityCheckGroupId = r.transitionSampleQualityCheckGroup.transitionQualityCheckGroupId;

        return {
          id: index++,
          transitionId: transitionId,
          transitionEnumId: transitionType,
          transition: configuredTransitionIds.map(id => transitionById[id ?? '']?.displayName),
          ...r.transitionSampleQualityCheckGroup,
          researchProjectId: researchProjectId,
          sampleTrackingTransitionSampleDialog: qualityCheckGroupId,
          sampleTrackingQualityCheckUndoDialog: undoableIds.includes(qualityCheckGroupId ?? '')
            ? qualityCheckGroupId
            : null,
          sampleTrackingTransitionFileDialog: qualityCheckGroupId,
          qualityCheckGroupId: qualityCheckGroupId,
          passedCount: passedCounts[qualityCheckGroupId]?.transitionSampleCounts,
          failedCount: failedCounts[qualityCheckGroupId]?.transitionSampleCounts,
          supplementalFileCounts: countsById[qualityCheckGroupId]?.supplementalFileCounts,
        };
      });

      return rows;
    },
    [researchProjectId]
  );

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (!(accessToken && researchProjectId !== undefined)) {
        return;
      }

      if (transitionPaths.length === 0) {
        return;
      }

      const [data, undoableIds, counts] = [
        await GetAllTransitionWithQualityCheckGroupsForResearchProject(researchProjectId, accessToken),
        await GetUndoableQualityCheckGroupIds(researchProjectId, accessToken),
        await GetQualityCheckGroupCounts(researchProjectId, accessToken),
      ];

      setRows(getRows(data, undoableIds, counts, transitionPaths));
    });
  }, [accessToken, researchProjectId, refreshTrigger, getRows, transitionPaths]);

  return (
    <>
      {loadingState.status === 'Complete' && (
        <DataGridPro
          rows={rows}
          columns={columns}
          density='compact'
          initialState={{
            sorting: {
              sortModel: [{ field: createdAt, sort: 'desc' }],
            },
          }}
          disableRowSelectionOnClick
        />
      )}
      <LoadingIndicator type={'Circular'} loadingState={loadingState} margin={'LR'} />
      <ErrorIndicator loadingState={loadingState} />
    </>
  );
};

const useColumns = (setRefreshTrigger: UseStateSetter<boolean>): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(
    () => [
      {
        field: transitionQualityCheckGroupId,
        headerName: t(transitionQualityCheckGroupId),
        headerAlign: 'center',
        align: 'center',
        width: 200,
        renderCell: params =>
          renderSampleTrackingTransitionSampleQualityCheckGroupScopedModalCell({
            ...params,
          }),
      },
      {
        field: createdAt,
        headerName: t(createdAt),
        headerAlign: 'left',
        align: 'left',
        width: 150,
        sortComparator: dateComparator,
        valueFormatter: dateFormatter,
      },
      {
        field: transition,
        headerName: t('qualityCheckType'),
        headerAlign: 'left',
        align: 'left',
        minWidth: 150,
        maxWidth: 400,
        flex: 1,
        valueFormatter: ({ value }) => ExtractDisplayValueFromPossibleArray(value, t),
      },
      {
        field: passedCount,
        headerName: t(passedCount),
        headerAlign: 'center',
        align: 'center',
        width: 150,
      },
      {
        field: failedCount,
        headerName: t(failedCount),
        headerAlign: 'center',
        align: 'center',
        width: 150,
      },
      {
        field: transitionId,
        headerName: t(transitionId),
        headerAlign: 'center',
        align: 'center',
        width: 150,
        valueFormatter: ({ value }) => value && truncateGuid(ExtractDisplayValueFromPossibleArray(value, t)),
      },
      {
        field: sampleTrackingQualityCheckUndoDialog,
        align: 'center',
        headerAlign: 'center',
        headerName: t('undo'),
        width: 75,
        renderCell: params =>
          renderSampleTrackingQualityCheckGroupUndoGridCell({
            ...params,
            onClose: () => setRefreshTrigger(refreshTrigger => !refreshTrigger),
          }),
      },
      {
        field: sampleTrackingTransitionFileDialog,
        align: 'center',
        headerAlign: 'center',
        headerName: t('numOfFiles'),
        width: 75,
        renderCell: params =>
          renderSampleTrackingSupplementalFilesGridCell({
            ...params,
            scope: 'QualityCheckGroup',
            numberOfFiles: params.row.supplementalFileCounts,
            transitionType: params.row.transition,
          }),
      },
    ],
    [t, setRefreshTrigger]
  );
};
